import type { Projectile, Tower, TowerType } from "@mmotdg/shared";
import * as THREE from "three";
import {
  buildProjectileModel,
  disposeProjectileModel,
  PROJECTILE_HEIGHT,
  type ProjectileModelView,
} from "./models/projectiles/index.js";

interface ProjView {
  group: THREE.Group;
  mesh: THREE.Mesh;
  type: TowerType;
  skinId: string | undefined;
  prevX: number;
  prevZ: number;
}

const FALLBACK_TYPE: TowerType = "archer";

export class ProjectileRenderer {
  private readonly group = new THREE.Group();
  private readonly views = new Map<string, ProjView>();

  private transform: import("./LaneTransform.js").LaneTransform | null = null;

  constructor(private readonly target: THREE.Object3D) {
    this.target.add(this.group);
  }

  setTransform(t: import("./LaneTransform.js").LaneTransform | null): void {
    this.transform = t;
  }

  private toWorld(x: number, y: number): { x: number; z: number } {
    if (this.transform) return this.transform.flatToWorld(x, y);
    return { x, z: y };
  }

  sync(projectiles: Projectile[], towersById: Map<string, Tower>): void {
    const seen = new Set<string>();
    for (const proj of projectiles) {
      seen.add(proj.id);
      if (!this.views.has(proj.id)) this.create(proj, towersById);
    }
    for (const [id, view] of this.views) {
      if (!seen.has(id)) {
        this.group.remove(view.group);
        disposeProjectileModel({ group: view.group, mesh: view.mesh });
        this.views.delete(id);
      }
    }
  }

  private create(proj: Projectile, towersById: Map<string, Tower>): ProjView {
    const tower = towersById.get(proj.towerId);
    const type = tower?.type ?? FALLBACK_TYPE;
    const skinId = tower?.skinId;
    const model: ProjectileModelView = buildProjectileModel(type, { skinId });
    {
      const pw = this.toWorld(proj.position.x, proj.position.y);
      model.group.position.set(pw.x, PROJECTILE_HEIGHT, pw.z);
    }
    this.group.add(model.group);
    const view: ProjView = {
      group: model.group,
      mesh: model.mesh,
      type,
      skinId,
      prevX: proj.position.x,
      prevZ: proj.position.y,
    };
    this.views.set(proj.id, view);
    return view;
  }

  render(projectiles: Projectile[], _dt: number): void {
    for (const proj of projectiles) {
      const view = this.views.get(proj.id);
      if (!view) continue;
      const pw = this.toWorld(proj.position.x, proj.position.y);
      view.group.position.x = pw.x;
      view.group.position.z = pw.z;
      const dx = pw.x - view.prevX;
      const dz = pw.z - view.prevZ;
      if (dx * dx + dz * dz > 1e-6) {
        view.group.rotation.y = Math.atan2(dx, dz);
      }
      view.prevX = pw.x;
      view.prevZ = pw.z;
    }
  }

  dispose(): void {
    for (const view of this.views.values()) {
      this.group.remove(view.group);
      disposeProjectileModel({ group: view.group, mesh: view.mesh });
    }
    this.views.clear();
    this.target.remove(this.group);
  }
}
